/**
 * 日曆 UI 專用的時間格式化工具（純函式，無 DOM／store 依賴）。
 * WorkingTime 的 start/end 是「距離當日 00:00 的分鐘數」，UI 用 HH:mm 顯示與輸入。
 */
import { minutes, type Minutes, type WorkingTime } from '@bigint-project/core';

export const MINUTES_PER_DAY = 1440;

/** 分鐘數 -> "HH:mm"。1440 顯示為 "24:00"（時段結束於午夜）。 */
export function minutesToHHmm(value: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, Math.round(value)));
  const hh = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** "HH:mm" -> 分鐘數；格式不合法回傳 null。 */
export function hhmmToMinutes(text: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (match === null) return null;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (mm < 0 || mm > 59) return null;
  const total = hh * 60 + mm;
  if (total < 0 || total > MINUTES_PER_DAY) return null;
  return total;
}

export function workingTime(start: number, end: number): WorkingTime {
  return { start: minutes(start) as Minutes, end: minutes(end) as Minutes };
}

/** 單一時段的工時（小時），用於清單顯示。 */
export function segmentHours(segment: WorkingTime): number {
  return ((segment.end as number) - (segment.start as number)) / 60;
}

/** 多段時段的工時總和（小時）。 */
export function totalHours(segments: readonly WorkingTime[]): number {
  return segments.reduce((sum, s) => sum + segmentHours(s), 0);
}
